<template>
  <div @click="selectedInputItem = null; selectedFermenterItem = null">
    <breadcrumbs home="Mash">
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <production-mashing-preferences @update="preferences=$event"/>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-overlay absolute :opacity="0.15" style="height:calc(100vh - 158px)" v-if="showWarning">
            <div style="position:relative;top:-100px;width:400px" class="d-inline-block purple white--text text-body-1 pa-5">
              Before you mash, you must have {{fermenters.length===0?'fermenters':'items'}} defined.
              <div class="mt-10 float-right">
                <v-btn :to="{name:'EquipmentFermenters'}" color="primary" small v-if="fermenters.length===0">Fermenters</v-btn>
                <v-btn :to="{name:'InventoryItems'}" color="primary" small v-else-if="items.length===0">Items</v-btn>
              </div>
            </div>
          </v-overlay>

          <v-row dense>
            <v-col cols="6" sm="4" md="3">
              <date-picker v-model="mash.date" @change="refresh();forDateAndSite();refreshLot()"/>
            </v-col>
            <v-col cols="6" sm="3" lg="2" v-if="preferences.mashDuration">
              <duration-picker label="Duration" v-model="mash.duration" dense/>
            </v-col>
            <v-col cols="4" sm="2" v-if="preferences.mashIncomplete">
              <v-checkbox label="Completed" v-model="mash.completed" dense class="mt-1" @change="validate()"/>
            </v-col>
            <v-col cols="4" sm="3" lg="2" :offset-md="3+(preferences.mashDuration?0:2)+(preferences.mashIncomplete?0:2)">
              <v-text-field label="#" v-model="mash.number" :rules="[fieldRules.required,fieldRules.maxLength(20)]" outlined dense :counter="20"/>
            </v-col>
          </v-row>
          <v-row dense v-if="sites.length>1" >
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete v-model="mash.siteId" outlined dense label="Site" :items="sites" item-text="name" item-value="id" @change="forDateAndSite"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-menu right max-width="300" nudge-bottom="12" :open-on-click="false" :close-on-click="false" :close-on-content-click="false" content-class="tour-arrow tour-arrow-left purple white--text pt-6 px-4" transition="slide-y-transition" :value="noMashBills">
                <template v-slot:activator="{on}">
                  <v-autocomplete v-model="mash.mashBillId" outlined dense label="Mash bill" :items="mashBills" item-text="name" item-value="id"
                                  @change="selectMashBill(false)" v-on="on" no-data-text="No mash bill found"/>
                </template>
                You can create mash bills for consistency and to save time.
                <v-btn plain class="text-none mt-4 white--text" :to="{name:'ProductionMashingMashBills'}">Go to mash bills</v-btn>
                <div class="mt-1">
                  <v-btn plain class="white--text mt-4 float-right" @click.stop="noMashBills=false">Okay</v-btn>
                </div>
              </v-menu>
            </v-col>
          </v-row>
          <v-row v-if="mashBill && mashBill.description" dense>
            <v-col cols="12" lg="8" class="primary--text mb-6">
              <em>{{mashBill.description}}</em>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="4" sm="3" lg="2">
              <decimal-number-input label="Volume" v-model="mash.volume" prefix="" suffix="litres" :precision="0" integer
                                    :disabled="(!adhoc && !mashBill.scalable) || !edit" @change="changeVolume"/>
            </v-col>
            <template v-if="preferences.mashpH">
              <v-col cols="4" sm="3" lg="2" v-if="preferences.mashBrix">
                <decimal-number-input label="Starting Brix" v-model="mash.osg" prefix="" suffix="°Bx" :precision="1" :max="100"/>
              </v-col>
              <v-col cols="4" sm="3" lg="2" v-else>
                <decimal-number-input label="Starting specific gravity" v-model="mash.osg" prefix="" suffix="" :precision="3" :min="1" :max="1.9"/>
              </v-col>
              <v-col cols="4" sm="3" lg="2">
                <decimal-number-input label="pH" v-model="mash.ph" prefix="" suffix="" :precision="1" :max="14"/>
              </v-col>
            </template>
          </v-row>
          <v-row dense v-if="preferences.mashLot">
            <v-col cols="4" sm="3" lg="2">
              <v-text-field v-model="mash.lot" label="Mash lot" outlined dense :rules="[fieldRules.required,fieldRules.maxLength(20)]" :counter="20"/>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" lg="8">
              <outlined-block v-model="mash.items" :rules="[itemsNeeded]" :error-messages="itemsError" ref="mashItems" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="mash.items" no-data-text="At least one input item is required"
                              :headers="[{text:'',value:'add',width:'10%',align:'center',cellClass:'pa-0',divider:true},
                                          {text:'Mash item input',value:'input',width:'45%',divider:true,class:'px-2',cellClass:'py-2 px-1'},
                                          {text:'Lot',value:'lot',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top'},
                                          {text:'Quantity',value:'quantity',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1',align:'right'}]"
                              class="no-sort"
                              :item-class="item => item.id=== selectedInputItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedInputItem = item.id; $nextTick(() => this.$refs.form.validate())}"
                              ref="itemTableBody">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n2" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n2" icon @click.stop="deleteItem(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.input="{item}">
                    <template v-if="selectedInputItem === item.id">
                      <multi-level-select v-if="categories.length" :menuData="eligibleItems" :second.sync="item.itemId" @change="changeItem(item)" outlined label="Input"/>
                      <v-autocomplete v-else :items="items" item-text="name" item-value="id" v-model="item.itemId" dense @change="changeItem(item)" :rules="[fieldRules.required]" class="text-body-2" autofocus outlined label="Input"/>
                    </template>
                    <div v-else class="ml-1" :class="{'error--text':!item.itemId}">{{mashItem(item).name}}</div>
                  </template>
                  <template v-slot:item.lot="{item}">
                    <template v-if="mashItem(item).lotTracking">
                      <v-combobox v-if="selectedInputItem === item.id" :items="currentLots(item)" v-model="item.lot" :rules="[lotValid,fieldRules.maxLength(20)]" dense class="text-body-2"
                                  outlined label="Lot" @change="changeItemLot(item);validate()"/>
                      <div v-else class="ml-1" :class="{'error--text':lotValid(item.lot)!==true}">{{lotValid(item.lot)!==true ? 'MISSING' : item.lot}}</div>
                    </template>
                    <div v-else class="ml-1">n/a</div>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <decimal-number-input v-if="selectedInputItem === item.id" label="Quantity" v-model="item.quantity" prefix=""
                          :suffix="mashItem(item).trackUnits" :precision="mashItem(item).precision" class="text-body-2"
                          :hint="mashId?'':mashItem(item).quantity" persistent-hint outlined/>
                    <div v-else class="mr-1" :class="{'error--text':item.quantity < 0}">
                      {{item.quantity | number(mashItem(item).precision)}} {{mashItem(item).trackUnits}}
                    </div>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedInputItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab :disabled="!edit || items.length===0" color="primary" x-small @click.stop="addItem">
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" lg="8">
              <outlined-block v-model="mash.fermenters" :rules="[fermentersNeeded]" :error-messages="fermentersError" ref="fermenterItems" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="mash.fermenters" no-data-text="At least one fermenter is required"
                              :headers="[{text:'',value:'add',width:'10%',align:'center',cellClass:'pa-0',divider:true},
                                          {text:'Fermenter',value:'output',width:'55%',divider:true,class:'px-2',cellClass:'py-2 px-1'},
                                          {text:'Volume',value:'volume',width:'30%',divider:true,class:'px-2',cellClass:'py-2 px-1',align:'right'}]"
                              class="no-sort"
                              :item-class="fermenter => fermenter.fermenterId=== selectedFermenterItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="fermenter => {selectedFermenterItem = fermenter.fermenterId; $nextTick(() => this.$refs.form.validate())}"
                              ref="fermenterTableBody">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n2" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n2" icon @click.stop="deleteFermenter(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.output="{item}">
                    <v-autocomplete v-if="selectedFermenterItem === item.fermenterId"
                                    dense label="Fermenter" v-model="item.fermenterId" :items="eligibleFermenters(item.fermenterId)" item-value="id" item-text="name" class="text-body-2"
                                    :hint="fermenterVolume(item.fermenterId)" persistent-hint :rules="[fieldRules.required]" outlined/>
                    <div v-else class="ml-1">{{getFermenter(item.fermenterId).name}}</div>
                  </template>
                  <template v-slot:item.volume="{item}">
                    <decimal-number-input v-if="selectedFermenterItem === item.fermenterId" label="Volume" v-model="item.volume" prefix=""
                          suffix="litres" :precision="0" integer class="text-body-2"/>
                    <div v-else class="mr-1" :class="{'error--text':item.volume < 0}">{{item.volume | number(0)}} litres</div>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedFermenterItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab :disabled="!edit || eligibleFermenters().length===0" color="primary" x-small @click.stop="addFermenter">
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" lg="8">
              <v-textarea label="Comments" rows="4"
                           v-model="mash.comments" outlined dense :rules="[fieldRules.maxLength(500)]" counter="500"/>
            </v-col>
          </v-row>

          <v-row v-if="mashId">
            <v-col>
              <v-btn outlined small @click="showNotes=true">View notes</v-btn>
            </v-col>
          </v-row>

        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-switch v-if="mashId && company.permissions.includes('production.write')" :disabled="dirty"
                  v-model="edit" label="Edit" hide-details class="mt-0" @change="forDateAndSite()"/>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <production-mashing-notes :mashId="mashId" v-model="showNotes" :allow-add="false"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format} from 'date-fns';
import sortableJS from 'sortablejs';
import confirmLeave from '@/mixins/confirmLeave'
import ProductionMashingNotes from '@/views/production/mashing/ProductionMashingNotes'
import ProductionMashingPreferences from '@/views/production/mashing/ProductionMashingPreferences'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'ProductionMashingEdit',
  mixins: [confirmLeave,createHierarchyList],
  components: {ProductionMashingNotes,ProductionMashingPreferences},
  props: {
    mashId: {type:String, required:false},
    mashBillId: {type:String, required:false},
    volume: {type:Number, required:false, default:1000},
    activityId: {type:String, required:false}
  },
  data () {
    return {
      edit: false,
      mash: {date:format(new Date(), 'yyyy-MM-dd HH:mm:ss'),number:'',completed:true,duration:0,volume:this.volume,osg:1.05,fsg:1,ph:5.5,lot:'',
             comments:'',items:[],fermenters:[],siteId:this.$store.getters.sites(true, {B266:true})[0].id,mashBillId:null,activityId:this.activityId},
      editingIncomplete: false,
      mashBrix: false,
      preferences: {},
      items: [],
      categories: [],
      mashBills: [{id:null,name:'AD HOC (no mash bill)',volume:0},{divider:true}],
      noMashBills: false,
      itemsError: '',
      fermenters: [],
      fermentersError: '',
      dirty: false,
      saving: false,
      loading: false,
      selectedInputItem: null,
      selectedFermenterItem: null,
      valid: true,
      lotValid: lot => !this.mash.completed || !!lot || 'Lot is mandatory',
      showNotes: false,
      sortableItem: null,
      sortableFermenter: null,
      showWarning: false
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.mashId, {B266:true});
    },
    adhoc() {
      return this.mash.mashBillId == null;
    },
    mashBill() {
      return this.mashBills.find(m => m.id === this.mash.mashBillId);
    },
    eligibleItems() {
      const categories = this.createHierarchyList(this.categories);
      for (const category of categories) {
        const sub = this.items.filter(item => item.category?.id === category.id);
        if (sub.length)
          category.menu = sub;
        else
          category.disabled = true;
      }
//      const items = [...categories.filter(category => !category.disabled)];
      const items = categories;
      const leftOver = this.items.filter(item => !item.category?.id);
      if (leftOver.length)
        items.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
      return items;
    }
  },
  watch: {
    mash: {
      handler() {
        this.dirty = true;
        this.checkItemQuantities();
        this.checkFermenters();
      },
      immediate: true,
      deep: true
    },
    'preferences.mashBrix'() {
      if (!this.mashBrix && this.preferences.mashBrix)
        this.mash.osg = this.$roundNumber((((182.4601 * this.mash.osg -775.6821) * this.mash.osg +1262.7794) * this.mash.osg -669.5622), 1);
      else if (this.mashBrix && !this.preferences.mashBrix)
        this.mash.osg = this.$roundNumber((this.mash.osg / (258.6-((this.mash.osg / 258.2)*227.1))) + 1, 3);
      this.mashBrix = this.preferences.mashBrix;
    }
  },
  async mounted() {
    this.loading = true;
    await Promise.all([
      this.$lazyListLoad(this.mashBills, 'mashBills2', 'id,name,volume,scalable', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.categories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(this.items, 'items2', 'id,name,sku,trackUnits,allowNegativeInventory,type,lotTracking,precision,category{id}', 100, !this.mashId ? [{field:'active', comparator:'eq', value:'true'}] : [])
    ]);
    const results = await this.$apiCall(`query ProductionMashingEdit($edit:Boolean!, $id:ID!) {
                                              mash(id:$id) @include(if:$edit) {
                                                id,date,number,created,site{id},completed,duration,mashBill{id},volume,osg,fsg,ph,lot,comments,
                                                items{itemId,quantity,lot},
                                                fermenters{fermenterId,volume}
                                              }
                                              mashingPreferences {
                                                mashBrix,mashDuration,mashIncomplete,mashpH,mashLot
                                              }
                                            }`,
                                        {edit:this.mashId!=undefined,id:this.mashId || ''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.preferences = results.data.mashingPreferences;
    this.mashBrix = this.preferences.mashBrix;
    if (this.mashId) {
      let id = Date.now();
      await this.$lazyListLoad(this.fermenters, 'fermenters2', `id,name,capacity,site{id}`, 100);
      results.data.mash.items.forEach(item => item.id = id++);
      this.mash = results.data.mash;
      this.mash.siteId = this.mash.site.id;
      delete this.mash.site;
      this.mash.mashBillId = this.mash.mashBill?.id ?? null;
      delete this.mash.mashBill;
      this.editingIncomplete = !this.mash.completed;
      if (!this.mash.completed)
        this.mash.duration = this.$roundNumber(Date.now()/60000 - this.mash.created, 0);
    }
    else
      await this.refresh();
    if (this.preferences.mashBrix)
      this.mash.osg = this.$roundNumber((((182.4601 * this.mash.osg -775.6821) * this.mash.osg +1262.7794) * this.mash.osg -669.5622), 1);

    this.noMashBills = this.mashBills.length === 2 && !this.$vuetify.breakpoint.mobile && this.fermenters.length && this.items.length;

    if (!this.mashId) {
      const results = await this.$apiCall(`query ProductionMashingEdit {
                                              mashNextNumber
                                            }`);
      if (!results.error)
        this.mash.number = results.data.mashNextNumber;
      this.edit = true;
      if (this.mashBills.length > 1 && this.mashBillId) {
        this.mash.mashBillId = this.mashBillId;
        await this.selectMashBill(!!this.volume);
      }
      else
        this.validate();
    }
    else {
      if (!this.mash.completed) {
        this.selectMashBill();
        this.edit = true;
      }
    }

    await this.refreshLot();
    this.$nextTick(() => this.dirty = false);

    this.loading = false;

    setTimeout(() => this.showWarning = this.fermenters.length===0 || this.items.length===0, 2_000);

    const _self = this;

    this.sortableItem = sortableJS.create(this.$refs.itemTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        if (newIndex === _self.mash.items.length) {
          console.log('nope')
          return;
        }
        const rowSelected = _self.mash.items.splice(oldIndex, 1)[0];
        _self.mash.items.splice(newIndex, 0, rowSelected);
      }
    })

    this.sortableFermenter = sortableJS.create(this.$refs.fermenterTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.mash.fermenters.splice(oldIndex, 1)[0];
        _self.mash.fermenters.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortableItem.destroy();
    this.sortableFermenter.destroy();
  },

  methods: {
    getFermenter(fermenterId) {
      return this.fermenters.find(fermenter => fermenter.id === fermenterId) ?? {name : 'MISSING', contents:{volume:0}};
    },
    async refresh() {
      if (this.mashId)
        return;
      this.fermenters = [];
      this.$lazyListLoad(this.fermenters, 'fermenters2', `id,name,capacity,site{id},contents(date:"${this.mash.date}") {volume}`, 100, [{field:'active', comparator:'eq', value:'true'}]);
    },
    validate() {
      this.$nextTick(() => {
        this.$refs.form?.validate();
      });
    },
    fermenterVolume(fermenterId) {
      if (this.mashId)
        return;
    return `${this.$number(this.getFermenter(fermenterId).contents.volume)} litres`;
    },
    mashItem(item) {
      return this.items.find(i => i.id === item.itemId) || {name:'MISSING', precision:0, trackUnits:''};
    },
    currentLots(item) {
      const itemObject = this.mashItem(item);
      return itemObject.sites ? itemObject.sites.filter(site => site.siteId === this.mash.siteId).map(site => site.lot) : [];
    },
    changeItemLot(item) {
      const itemObject = this.mashItem(item);
      const site = itemObject.sites && itemObject.sites.find(site => site.siteId === this.mash.siteId && site.lot === item.lot);
      itemObject.quantity = `${site ? this.$number(site.quantity, itemObject.precision) : '0'} ${itemObject.trackUnits}`;
    },
    changeVolume() {
      if (this.mashBill && this.mashBill.scalable)
        this.selectMashBill(true);
    },
    checkFermenters() {
      if (!this.mash.completed)
        return true;
      // Make sure any fermenter specified exists,
      // its volume doesn't exceed its capacity, and the total volume matches the mash volume
      this.fermentersError = '';
      let totalVolume = 0;
      for (let i = this.mash.fermenters.length-1; i >= 0; i--) {    // Do it this way since we may have to remove element
        const fermenter = this.mash.fermenters[i];
        if (fermenter.fermenterId === '')   // Unspecified fermenter line - no need to do checking
          return;
        const fermenterObject = this.fermenters.find(f => f.id === fermenter.fermenterId && f.site.id === this.mash.siteId);
        if (!fermenterObject)               // Cannot find fermenter
          this.mash.fermenters.splice(i, 1);
        else
          totalVolume += fermenter.volume;
      }
      if (this.fermentersError === '' && this.fermenters.length !== 0 && this.mash.fermenters.length !== 0 && totalVolume < this.mash.volume)
        this.fermentersError = `Total fermenter volume (${totalVolume} litres) must be at least the mash volume (${this.mash.volume} litres)`;
    },

    // Verify specified quantities against mash bill requirements
    checkItemQuantities() {
      if (!this.mash.completed)
        return true;

      if (!this.mash.mashBillId)    // No mash bill, so nothing to check
        return true;

      if (!this.mashBill.items)
        return true;

      this.itemsError = '';

      const totalMashItems = [];
      for (const mashItem of this.mash.items) {
        const o = {id:mashItem.itemId,quantity:mashItem.quantity};
        const totalMashItem = totalMashItems.find(totalMashItem => totalMashItem.id === o.id);
        if (totalMashItem)
          totalMashItem.quantity += o.quantity;
        else
          totalMashItems.push(o);
      }

      for (const mashBillItem of this.mashBill.items) {
        const mashItem = totalMashItems.find(i => i.id === mashBillItem.itemId);
        const itemObject = this.mashItem(mashBillItem);
        let quantity = this.$roundNumber(mashBillItem.minQuantity * this.mash.volume / this.mashBill.volume, mashBillItem.itemPrecision);
        if (!mashItem && mashBillItem.minQuantity !== 0 ||
            mashItem && mashItem.quantity < quantity) {
          this.itemsError = `Mash bill requires a minimum of ${quantity} ${itemObject.trackUnits} of ${itemObject.name}`;
          break;
        }
        if (mashBillItem.maxQuantity === 0)
          continue;
        quantity = this.$roundNumber(mashBillItem.maxQuantity * this.mash.volume / this.mashBill.volume, mashBillItem.itemPrecision);
        if (mashItem?.quantity > quantity) {
          this.itemsError = `Mash bill requires a maximum of ${quantity} ${itemObject.trackUnits} of ${itemObject.name}`;
          break;
        }
      }
    },
    itemsNeeded(items) {
      if (!this.mash.completed)    // No checking necessary on an incomplete mash
        return true;
      if (items.length === 0)
        return 'At least one item needs to be added to the mash';

      return true;
    },
    fermentersNeeded(fermenters) {
      if (!this.mash.completed)    // No checking necessary on an incomplete mash
        return true;
      if (fermenters.length === 0)
        return 'At least one fermenter needs to be used for the mash';

      return true;
    },

    async changeItem(mashItem) {
      const item = this.mashItem(mashItem);
      if (item.id && !item.sites) {
        const results = await this.$apiCall(`query ProductionMashingEdit($id:ID!, $date:DateTime!) {
                                                  item(id:$id) {
                                                    sites(asof:$date) {siteId,lot,quantity}
                                                  }
                                                }`,
                                            {id:mashItem.itemId,date:this.mash.date});
        if (!results.error)
          this.$set(item, 'sites', results.data.item.sites);
      }
      if (!this.mashId)
        mashItem.lot = null;
      this.validate();
    },

    async forDateAndSite() {
      this.loading = true;
      for (const item of this.mash.items) {
        const itemObject = this.mashItem(item);

        // Get proper quantity and lot info for current date and site
        delete itemObject.sites;
        await this.changeItem(item);

        // Make sure lot is still valid, unless this is an edit where we'll assume it must be valid
        if (!this.mashId && !this.currentLots(item).some(lot => lot === item.lot)) {
          item.lot = '';
          itemObject.quantity = '';
        }
      }
      this.loading = false;

      this.checkItemQuantities();
      this.checkFermenters();
      this.validate();
    },
    async selectMashBill(keepVolume = false) {
      if (!this.mash.mashBillId)
        return;
      if (!this.mashBill.items) {
        this.loading = true;
        const results = await this.$apiCall(`query ProductionMashingEdit($id:ID!) {
                                                  mashBill(id:$id) {
                                                    description,items{itemId,quantity,minQuantity,maxQuantity,itemPrecision}
                                                  }
                                                }`,
                                            {id:this.mashBill.id});
        if (!results.error) {
          this.$set(this.mashBill, 'items', results.data.mashBill.items);
          this.$set(this.mashBill, 'description', results.data.mashBill.description);
        }
        this.loading = false;
      }

      // Note that when editing an incomplete mash using a mashbill
      // the items are still replaced, meaning quantity and lot information is lost
      if (!this.mashId /*|| this.editingIncomplete*/) {
        if (!keepVolume)
          this.mash.volume = this.mashBill.volume;
        this.mash.items.splice(0, this.mash.items.length);
        let id = Date.now();
        for (const item of this.mashBill.items) {
          const mashItem = {id:id++,itemId:item.itemId,quantity:this.$roundNumber(item.quantity * this.mash.volume / this.mashBill.volume, item.itemPrecision)};
          this.mash.items.push(mashItem);
          await this.changeItem(mashItem);
        }
      }
      else
        for (const item of this.mash.items)
          await this.changeItem(item);

      this.validate();
    },
    eligibleFermenters(includeFermenterId) {
      return this.fermenters.filter(fermenter => fermenter.site.id === this.mash.siteId && (fermenter.id === includeFermenterId || !this.mash.fermenters.some(f => f.fermenterId === fermenter.id)));
    },
    async addItem() {
      const mashItem = {id:Date.now(),itemId:'',lot:null, quantity:0};
      this.mash.items.push(mashItem);
      await this.changeItem(mashItem);
      this.selectedInputItem = mashItem.id;
      this.validate();
    },
    deleteItem(index) {
      this.mash.items.splice(index, 1);
    },
    addFermenter() {
      const fermenterId = this.eligibleFermenters()[0].id;
      this.mash.fermenters.push({fermenterId,volume:this.mash.volume,id:Date.now()});
      this.selectedFermenterItem = fermenterId;
      this.validate();
    },
    deleteFermenter(index) {
      this.mash.fermenters.splice(index, 1);
    },
    async refreshLot() {
      if (this.mashId)    // Don't do for editing existing mash
        return;
      this.loading = true;
      const results = await this.$apiCall(`query ProductionMashingEdit($asof:DateTime!) {
                                            lotUnique(prefix:"M",date:$asof)
                                          }`,
                                          {asof:this.mash.date});
      if (!results.error)
        this.mash.lot = results.data.lotUnique;

      this.loading = false;
    },
    cancel() {
      this.$router.back();
    },
    async save() {
      const incomplete = this.mash.items.some(item => !item.itemId ||
                                                      (this.mashItem(item).lotTracking && this.lotValid(item.lot) !== true) ||
                                                      item.quantity < 0) ||
                         this.mash.fermenters.some(fermenter => fermenter.volume < 0);
      if (!this.$refs.form.validate() || incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {items, fermenters, created, ...input} = this.mash;
      input.items = this.mash.items.map(item => {
        // eslint-disable-next-line no-unused-vars
        const {id,open,incomplete,...itemData} = item;
        return itemData;
      });
      input.fermenters = this.mash.fermenters.map(fermenter => {
        // eslint-disable-next-line no-unused-vars
        const {id,open,incomplete,...fermenterData} = fermenter;
        return fermenterData;
      });
      if (this.preferences.mashBrix)
        input.osg = this.$roundNumber((input.osg / (258.6-((input.osg / 258.2)*227.1))) + 1, 3);
      const results = await this.$apiCall(`mutation ProductionMashingEdit($input: MashInput!) {
                                             mashCreate(input:$input) {id}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.mash.id?'Updated':'Created'} mash`});
        if (this.activityId)
          this.$store.dispatch('checkTasks', true);
      }
    }
  }
}
</script>